import { IntegrationDataType } from '@bbkAdminRedux/data-management/reducers';
import {
  CtaProcessingOption,
  type CtaDetails,
} from '@bbkAdminUtils/api-client/rest-modals-cards-types';
import {
  OfferCategory,
  OfferCtaSubcategory,
} from '@bbkAdminUtils/api-client/rest-offers-api';
import {
  PeriodUnit,
  type Price,
} from '@bbkPricingPage/types/pricing-table-types';

export const DYNAMIC_OFFER_FIELD_NAMESPACE = 'dynamic_offer';
export const DYNAMIC_OFFER_ID_PLACEHOLDER = 'offer_id';
export const DYNAMIC_OFFER_NEW_PRICE_KEY = 'new_price';
export const DYNAMIC_OFFER_SAVINGS_KEY = 'savings';
export const DYNAMIC_OFFER_NEW_PRICE_LABEL = 'New Price';
export const DYNAMIC_OFFER_SAVINGS_LABEL = 'Savings';

const DYNAMIC_OFFER_FIELD_LABELS: Record<string, string> = {
  [DYNAMIC_OFFER_NEW_PRICE_KEY]: DYNAMIC_OFFER_NEW_PRICE_LABEL,
  [DYNAMIC_OFFER_SAVINGS_KEY]: DYNAMIC_OFFER_SAVINGS_LABEL,
};

export const toDynamicOfferFieldLabel = (fieldKey: string): string =>
  DYNAMIC_OFFER_FIELD_LABELS[fieldKey] ?? fieldKey;

export const toDynamicOfferFieldKey = (
  offerId: string,
  fieldName: string
): string => `${DYNAMIC_OFFER_FIELD_NAMESPACE}.${offerId}.${fieldName}`;

export const parseDynamicOfferFieldKey = (
  identifier: string | undefined
): { offerId: string; fieldName: string } | undefined => {
  if (!identifier?.startsWith(`${DYNAMIC_OFFER_FIELD_NAMESPACE}.`)) {
    return undefined;
  }
  const rest = identifier.slice(DYNAMIC_OFFER_FIELD_NAMESPACE.length + 1);
  const separator = rest.indexOf('.');
  if (separator <= 0 || separator === rest.length - 1) return undefined;
  return {
    offerId: rest.slice(0, separator),
    fieldName: rest.slice(separator + 1),
  };
};

export const isDynamicOfferFieldKey = (
  identifier: string | undefined
): identifier is string => !!parseDynamicOfferFieldKey(identifier);

export const AUTO_CURRENCY_VALUE = '';

const PERIOD_UNIT_LABELS: Record<PeriodUnit, { one: string; many: string }> = {
  [PeriodUnit.day]: { one: 'Daily', many: 'days' },
  [PeriodUnit.week]: { one: 'Weekly', many: 'weeks' },
  [PeriodUnit.month]: { one: 'Monthly', many: 'months' },
  [PeriodUnit.year]: { one: 'Yearly', many: 'years' },
};

const isPeriodUnit = (value: string): value is PeriodUnit =>
  (Object.values(PeriodUnit) as string[]).includes(value);

export const isDynamicPlanChangeCta = (
  details: Partial<CtaDetails> | undefined,
  featureEnabled: boolean,
  integrationDataType?: IntegrationDataType
): boolean =>
  featureEnabled &&
  integrationDataType === IntegrationDataType.CHARGEBEE &&
  details?.category === OfferCategory.plan_change &&
  details?.processing_option === CtaProcessingOption.billing &&
  details?.sub_category !== OfferCtaSubcategory.add_on;

/** Drop item/frequency/toggle fields that only apply to billing plan-change CTAs. */
export const clearDynamicPlanChangeCtaFields = (
  details: Partial<CtaDetails> | undefined,
  options?: { clearPlanChangeCurrency?: boolean }
): void => {
  if (!details) return;
  details.billing_system_item_id = undefined;
  details.period = undefined;
  details.period_unit = undefined;
  details.show_target_price_monthly = undefined;
  details.show_savings_monthly = undefined;
  if (
    options?.clearPlanChangeCurrency &&
    details.category === OfferCategory.plan_change
  ) {
    details.pinned_currency = undefined;
  }
};

export const toFrequencyKey = (
  period: number,
  periodUnit: PeriodUnit
): string => `${period}::${periodUnit}`;

export const parseFrequencyKey = (
  key: string | undefined
): { period: number; period_unit: PeriodUnit } | undefined => {
  if (!key) return undefined;
  const [periodStr, periodUnit] = key.split('::');
  const period = Number(periodStr);
  if (
    !periodStr ||
    !periodUnit ||
    Number.isNaN(period) ||
    !isPeriodUnit(periodUnit)
  ) {
    return undefined;
  }
  return { period, period_unit: periodUnit };
};

export const formatFrequencyLabel = (key: string): string => {
  const parsed = parseFrequencyKey(key);
  if (!parsed) return key;
  const labels = PERIOD_UNIT_LABELS[parsed.period_unit];
  if (parsed.period === 1) {
    return labels?.one ?? `Every ${parsed.period_unit}`;
  }
  const many = labels?.many ?? `${parsed.period_unit}s`;
  return `Every ${parsed.period} ${many}`;
};

export const findMatchingItemPrice = (
  prices: Price[],
  args: {
    itemId: string;
    period: number;
    periodUnit: PeriodUnit;
    currency: string;
  }
): Price | undefined => {
  const matches = prices.filter(
    (price) =>
      price.billing_system_item_id === args.itemId &&
      price.period === args.period &&
      price.period_unit === args.periodUnit &&
      price.currency === args.currency
  );
  return matches.find((price) => price.active !== false) ?? matches[0];
};

export const findItemPriceById = (
  prices: Price[],
  planId: string
): Price | undefined =>
  prices.find(
    (price) => price.billing_system_id === planId || price.id === planId
  );
